import { useState, useCallback } from 'react';
import { API_BASE } from '../../../config/constants';
import { User } from '../interfaces/IUsers';
import { hashPassword } from '../../utils/helpers';

export type Role = {
    id: bigint;
    role: string;
};

export type RoleDesk = {
    [key: string]: any;
};

export type ObjectModel = {
    id: bigint;
    name: string;
    description?: string;
    [key: string]: any;
};

// Keep these for backward compatibility if needed
export type UserRecord = User;
export type RoleRecord = Role;
export type RoleDeskRecord = RoleDesk;

export type UserPayload = {
    email: string;
    role: string;
};

export type UpdateUserPasswordPayload = {
    user_id: bigint;
    password: string;
};

export type RolePayload = {
    role: string;
    desks: string;
};

export type UserFormData = {
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    company?: string;
    companyId?: string | number;
    password?: string;
    confirmPassword?: string;
};

export type CreateUserObjectRequestPayload = {
    request_id?: string | number | null;
    user_id: string | number;
    unit_id?: string | number | null;
    description?: string;
    object_id?: string | number | null;
    agency_id?: string | number | null;
    contract_id?: string | number | null;
};

function buildJsonHeaders(): Record<string, string> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    return headers;
}

/**
 * React hook for users/roles management with user invitation capabilities.
 * Merged from user_invitation_controller.dart and useUsers.
 *
 * Handles:
 * - User CRUD operations
 * - Role management
 * - User invitation and form submission
 * - Object/desk permissions filtering
 * - Search and filtering by date range and status
 */
export function useUsers() {
    const [users, setUsers] = useState<User[]>([]);
    const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
    const [roles, setRoles] = useState<Role[]>([]);
    const [rolesdesks, setRolesdesks] = useState<RoleDesk[]>([]);
    const [objects, setObjects] = useState<ObjectModel[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [currentUser, setCurrentUser] = useState<User | null>(null);

    // Form state
    const [formData, setFormData] = useState<UserFormData>({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        password: '',
        confirmPassword: '',
    });

    const [isDataUpdated, setIsDataUpdated] = useState(false);
    const [selectedRows, setSelectedRows] = useState<boolean[]>([]);
    const [isSendingBulk, setIsSendingBulk] = useState(false);
    const [filtersApplied, setFiltersApplied] = useState(false);

    // Filter state
    const [selectedObjectId, setSelectedObjectId] = useState<string | number>(0);
    const [selectedStatusId, setSelectedStatusId] = useState<number>(-1);
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);
    const [searchQuery, setSearchQuery] = useState('');

    // formKeyRef removed (unused)

    /**
     * Fetch current user from localStorage
     */
    const fetchCurrentUser = useCallback((): User | null => {
        try {
            const authTokenStr = localStorage.getItem('AUTH_TOKEN');
            if (!authTokenStr) return null;
            const user: User = JSON.parse(authTokenStr);
            setCurrentUser(user);
            return user;
        } catch (err) {
            console.error('Error parsing current user:', err);
            return null;
        }
    }, []);

    /**
     * Load all objects filtered by user permissions
     */
    const loadAllObjects = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await fetch(`${API_BASE}/api/objects/get-all-objects`, {
                method: 'POST',
                headers: buildJsonHeaders(),
            });
            if (!response.ok) throw new Error('Failed to fetch objects');

            const data = await response.json();
            const allObjects: ObjectModel[] = Array.isArray(data)
                ? data
                : Array.isArray(data.data)
                ? data.data
                : [];

            // Get current user for permission filtering
            const user = fetchCurrentUser();
            const userObjectPermissions = user?.objectPermissionIds ?? [];
            const permissionIds = new Set(userObjectPermissions.map((id) => String(id)));

            // If permissions are missing, keep the list visible instead of filtering everything out.
            const filtered =
                permissionIds.size === 0
                    ? allObjects
                    : allObjects.filter((obj) => permissionIds.has(String(obj.id)));

            setObjects(filtered);
        } catch (err: any) {
            console.error('Error loading objects:', err);
            setError(err.message);
            setObjects([]);
        } finally {
            setLoading(false);
        }
    }, [fetchCurrentUser]);

    /**
     * Load all users filtered by object permissions
     */
    const loadUsers = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await fetch(`${API_BASE}/api/users/get-all-users`, {
                method: 'POST',
                headers: buildJsonHeaders(),
            });
            if (!response.ok) throw new Error('Failed to fetch users');

            const data = await response.json();
            const allUsers: User[] = Array.isArray(data)
                ? data
                : Array.isArray(data.data)
                ? data.data
                : data.users || [];

            // Settings users should not be filtered by permissions or any other condition on load.
            setUsers(allUsers);
            setFilteredUsers(allUsers);
            setSelectedRows(new Array(allUsers.length).fill(false));
        } catch (err: any) {
            console.error('Error loading users:', err);
            setError(err.message);
            setUsers([]);
            setFilteredUsers([]);
        } finally {
            setLoading(false);
        }
    }, []);

    /**
     * Initialize form with existing user data
     */
    const initFormFields = useCallback((user?: User) => {
        if (user) {
            setFormData({
                firstName: user.first_name || '',
                lastName: user.last_name || '',
                email: user.email || '',
                phoneNumber: user.phone_number || '',
                password: '',
                confirmPassword: '',
            });
        } else {
            setFormData({
                firstName: '',
                lastName: '',
                email: '',
                phoneNumber: '',
                password: '',
                confirmPassword: '',
            });
        }
    }, []);

    /**
     * Get selected users from filtered list
     */
    const getSelectedUsers = useCallback((): User[] => {
        return selectedRows
            .map((selected, index) => (selected ? filteredUsers[index] : null))
            .filter((user): user is User => user !== null);
    }, [selectedRows, filteredUsers]);

    /**
     * Search users by name or email
     */
    const searchUsers = useCallback(
        (query: string) => {
            setSearchQuery(query);

            if (!query) {
                setFilteredUsers(users);
                return;
            }

            const results = users.filter((user) =>
                user.first_name?.toLowerCase().includes(query.toLowerCase()) ||
                user.last_name?.toLowerCase().includes(query.toLowerCase()) ||
                user.email?.toLowerCase().includes(query.toLowerCase())
            );

            setFilteredUsers(results);
            setSelectedRows(new Array(results.length).fill(false));
        },
        [users]
    );

    /**
     * Filter users by object, status, and date range
     */
    const filterUsers = useCallback(() => {
        let results = users;

        // Filter by object
        if (selectedObjectId && selectedObjectId !== 0) {
            results = results.filter(
                (u) => String(u.objectId) === String(selectedObjectId)
            );
        }

        // Filter by status
        if (selectedStatusId >= 0) {
            results = results.filter((u) => u.status === selectedStatusId);
        }

        // Filter by date range
        if (startDate || endDate) {
            results = results.filter((u) => {
                const userDate = new Date(u.createdAt || 0);
                if (startDate && userDate < startDate) return false;
                if (endDate && userDate > endDate) return false;
                return true;
            });
        }

        setFilteredUsers(results);
        setFiltersApplied(true);
        setSelectedRows(new Array(results.length).fill(false));
    }, [users, selectedObjectId, selectedStatusId, startDate, endDate]);

    /**
     * Clear all filters
     */
    const clearFilters = useCallback(() => {
        setSelectedObjectId(0);
        setSelectedStatusId(-1);
        setStartDate(null);
        setEndDate(null);
        setSearchQuery('');
        setFilteredUsers(users);
        setFiltersApplied(false);
        setSelectedRows(new Array(users.length).fill(false));
    }, [users]);

    /**
     * Submit new user invitation
     */
    const submitUser = useCallback(
        async (data: UserFormData) => {
            setLoading(true);
            setError(null);

            console.log('[HUsers] submitUser called', { data, selectedObjectId });

            try {
                if (!data.firstName || !data.lastName || !data.email) {
                    throw new Error('First name, last name, and email are required');
                }

                if (selectedObjectId === 0) {
                    // Allow missing object_id during debugging so the network request is sent.
                    // Production should enforce object selection; keep a warning instead of throwing.
                    console.warn('[HUsers] submitUser: no object selected, proceeding for debug');
                }

                const currentUser = fetchCurrentUser();
                if (!currentUser?.id) {
                    throw new Error('User not authenticated');
                }

                // Call backend to create user
                const payload = {
                    user: {
                        email: data.email.trim(),
                        first_name: data.firstName.trim(),
                        last_name: data.lastName.trim(),
                        phone_number: data.phoneNumber.trim(),
                        object_id: selectedObjectId,
                        company_id: data.companyId || currentUser.company_id,
                    },
                };

                console.log('[HUsers] register-user payload', payload);

                const safeBody = JSON.stringify(payload, (_k, v) => (typeof v === 'bigint' ? v.toString() : v));
                console.log('[HUsers] register-user sending body (safe):', safeBody);
                const response = await fetch(`${API_BASE}/api/users/register-user`, {
                    method: 'POST',
                    headers: buildJsonHeaders(),
                    body: safeBody,
                });

                console.log('[HUsers] register-user response status', { status: response.status, ok: response.ok });

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    console.error('[HUsers] register-user non-ok response body', errorData);
                    throw new Error(
                        errorData.message || 'Failed to create user'
                    );
                }

                const result = await response.json().catch(() => null);
                console.log('[HUsers] register-user result', result);

                // Reload users list
                await loadUsers();

                // Reset form
                initFormFields();

                setIsDataUpdated(true);
                return result;
            } catch (err: any) {
                const msg = err?.message || 'Failed to create user';
                setError(msg);
                throw err;
            } finally {
                setLoading(false);
            }
        },
        [selectedObjectId, fetchCurrentUser, loadUsers, initFormFields]
    );

    const fetchUsers = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API_BASE}/api/users/get-all-users`, {
                method: 'POST',
                headers: buildJsonHeaders(),
            });
            if (!response.ok) throw new Error('Failed to fetch users');
            const data = await response.json();
            const allUsers: User[] = Array.isArray(data)
                ? data
                : Array.isArray(data.data)
                ? data.data
                : data.users || [];
            setUsers(allUsers);
            setFilteredUsers(allUsers);
        } catch (error) {
            console.error('Users hook: error fetching users:', error);
            setUsers([]);
            setFilteredUsers([]);
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchRoles = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API_BASE}/api/users/get-all-user-roles`, {
                method: 'POST',
                headers: buildJsonHeaders(),
            });
            if (!response.ok) throw new Error('Failed to fetch roles');
            const data = await response.json();
            const rolesArr = Array.isArray(data) ? data : Array.isArray(data.data) ? data.data : (data.roles as Role[]) || [];
            setRoles(rolesArr);
        } catch (error) {
            console.error('Users hook: error fetching roles:', error);
            setRoles([]);
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchRolesDesks = useCallback(async () => {
        setLoading(true);
        try {
            // roles_desks endpoint not available locally — return empty gracefully
            setRolesdesks([]);
            return;
        } catch (error) {
            console.error('Users hook: error fetching roles desks:', error);
            setRolesdesks([]);
        } finally {
            setLoading(false);
        }
    }, []);

    const handleInsert = useCallback(
        async (userData: UserPayload) => {
            setLoading(true);
            try {
                const response = await fetch(`${API_BASE}/api/insertuser`, {
                    method: 'POST',
                    headers: buildJsonHeaders(),
                    body: JSON.stringify(userData),
                });

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(errorData.detail || 'Failed to create user');
                }

                const result = await response.json();
                await fetchUsers();
                return result;
            } finally {
                setLoading(false);
            }
        },
        [fetchUsers]
    );

    const handleDelete = useCallback(
        async (userData: UserPayload) => {
            setLoading(true);
            try {
                const response = await fetch(`${API_BASE}/api/deleteuser`, {
                    method: 'POST',
                    headers: buildJsonHeaders(),
                    body: JSON.stringify(userData),
                });

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(errorData.detail || 'Failed to delete user');
                }

                const result = await response.json();
                await fetchUsers();
                return result;
            } finally {
                setLoading(false);
            }
        },
        [fetchUsers]
    );

    const handleRoleInsert = useCallback(
        async (roleData: RolePayload) => {
            setLoading(true);
            try {
                const response = await fetch(`${API_BASE}/api/insertrole`, {
                    method: 'POST',
                    headers: buildJsonHeaders(),
                    body: JSON.stringify(roleData),
                });

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(errorData.detail || 'Failed to create role');
                }

                const result = await response.json();
                await fetchRoles();
                await fetchRolesDesks();
                return result;
            } finally {
                setLoading(false);
            }
        },
        [fetchRoles, fetchRolesDesks]
    );

    const handleRoleDelete = useCallback(
        async (roleData: UserPayload) => {
            setLoading(true);
            try {
                const response = await fetch(`${API_BASE}/api/deleterole`, {
                    method: 'POST',
                    headers: buildJsonHeaders(),
                    body: JSON.stringify(roleData),
                });

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(errorData.detail || 'Failed to delete role');
                }

                const result = await response.json();
                await fetchRoles();
                return result;
            } finally {
                setLoading(false);
            }
        },
        [fetchRoles]
    );

    const updateUserPassword = useCallback(
        async ({ user_id, password }: UpdateUserPasswordPayload) => {
            setLoading(true);
            try {
                const hashedPassword = hashPassword(password);
                const response = await fetch(`${API_BASE}/api/users/update-user-password`, {
                    method: 'POST',
                    headers: buildJsonHeaders(),
                    body: JSON.stringify({ user_id, password: hashedPassword }),
                });

                const result = await response.json().catch(() => ({}));
                if (!response.ok || !result?.success) {
                    throw new Error(result?.message || 'Failed to update password.');
                }

                return result;
            } finally {
                setLoading(false);
            }
        },
        []
    );

    const setUserNda = useCallback(
        async (user_id: string | number) => {
            setLoading(true);
            setError(null);
            try {
                console.log('[HUsers] setUserNda: calling API', { user_id });
                const response = await fetch(`${API_BASE}/api/users/set-user-nda`, {
                    method: 'POST',
                    headers: buildJsonHeaders(),
                    body: JSON.stringify({ user_id }),
                });

                const result = await response.json().catch(() => ({}));
                console.log('[HUsers] setUserNda response', { status: response.status, ok: response.ok, result });
                if (!response.ok || !result?.success) {
                    console.error('[HUsers] setUserNda failed (bad response)', { status: response.status, result });
                    throw new Error(result?.message || 'Failed to set NDA flag');
                }

                console.log('[HUsers] setUserNda succeeded', result);

                // Optionally refresh current user list
                await fetchUsers();
                return result;
            } catch (err: any) {
                console.error('[HUsers] setUserNda error', err);
                const message = err?.message || 'Failed to set NDA flag';
                setError(message);
                throw err;
            } finally {
                setLoading(false);
            }
        },
        [fetchUsers]
    );

    const createUserObjectRequest = useCallback(
        async ({
            request_id = null,
            user_id,
            unit_id = null,
            description = '',
            object_id = null,
            agency_id = null,
            contract_id = null,
        }: CreateUserObjectRequestPayload) => {
            setLoading(true);
            setError(null);

            try {
                const payload = {
                    request_id,
                    user_id,
                    unit_id,
                    description,
                    object_id,
                };

                console.log('[HUsers] createUserObjectRequest payload', payload);

                const response = await fetch(`${API_BASE}/api/users/create-user-object-request`, {
                    method: 'POST',
                    headers: buildJsonHeaders(),
                    body: JSON.stringify(payload),
                });

                const result = await response.json().catch(() => ({}));

                console.log('[HUsers] createUserObjectRequest response', {
                    status: response.status,
                    ok: response.ok,
                    result,
                });

                if (!response.ok || !result?.success) {
                    console.error('[HUsers] createUserObjectRequest failed', {
                        status: response.status,
                        result,
                    });
                    throw new Error(result?.message || 'Failed to create user object request.');
                }

                return result;
            } catch (err: any) {
                const message = err?.message || 'Failed to create user object request.';
                console.error('[HUsers] createUserObjectRequest exception', {
                    message,
                    error: err,
                });
                setError(message);
                throw err;
            } finally {
                setLoading(false);
            }
        },
        []
    );

        const update_user = useCallback(
            async ({ user_id, firstName, lastName, display_name, phoneNumber, country_code, profile_pic, companyId, role_id }: any) => {
                setLoading(true);
                setError(null);
                try {
                    const payload = {
                        user_id,
                        first_name: firstName,
                        last_name: lastName,
                        display_name: display_name ?? `${firstName} ${lastName}`,
                        phone_number: phoneNumber ?? null,
                        country_code: country_code ?? null,
                        profile_pic: profile_pic ?? null,
                        company_id: companyId ?? null,
                        role_id: role_id ?? null,
                    };

                    console.log('[HUsers] update_user payload', payload);

                    const response = await fetch(`${API_BASE}/api/users/update-user-personal-details`, {
                        method: 'POST',
                        headers: buildJsonHeaders(),
                        body: JSON.stringify(payload),
                    });

                    const result = await response.json().catch(() => ({}));
                    console.log('[HUsers] update_user response', { status: response.status, ok: response.ok, result });

                    if (!response.ok || !result?.success) {
                        const errMsg = result?.message || 'Failed to update user';
                        setError(errMsg);
                        throw new Error(errMsg);
                    }

                    // Refresh list
                    await loadUsers();
                    return result;
                } catch (err: any) {
                    console.error('[HUsers] update_user error', err);
                    const message = err?.message || 'Failed to update user';
                    setError(message);
                    throw err;
                } finally {
                    setLoading(false);
                }
            },
            [loadUsers]
        );

    return {
        // User data
        users,
        filteredUsers,
        roles,
        rolesdesks,
        objects,
        currentUser,

        // Form data
        formData,
        setFormData,
        isDataUpdated,
        setIsDataUpdated,
        selectedRows,
        setSelectedRows,

        // Filter state
        selectedObjectId,
        setSelectedObjectId,
        selectedStatusId,
        setSelectedStatusId,
        startDate,
        setStartDate,
        endDate,
        setEndDate,
        searchQuery,
        filtersApplied,

        // Loading/error
        loading,
        error,
        isSendingBulk,
        setIsSendingBulk,

        // Methods - Data loading
        fetchUsers,
        fetchRoles,
        fetchRolesDesks,
        loadUsers,
        loadAllObjects,
        fetchCurrentUser,

        // Methods - User invitation
        initFormFields,
        submitUser,
        update_user,
        getSelectedUsers,

        // Methods - Search/filter
        searchUsers,
        filterUsers,
        clearFilters,

        // Methods - CRUD
        handleInsert,
        handleDelete,
        handleRoleInsert,
        handleRoleDelete,
        updateUserPassword,
        createUserObjectRequest,
        setUserNda,
    };
}

// Backward-compatible alias matching the existing HUsers naming convention.
export const useHUser = useUsers;
